import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "@helpers/translate";
import { ReactComponent as CheckboxCheckedIcon } from "@assets/images/icons/checkbox-checked.svg";
import { TradeModal } from "./TradeModal";
import { usePendingAction } from "./hooks/usePendingAction";
import { useTradeConfirmSkips } from "./hooks/useTradeConfirmSkips";
import "./ConfirmTradeModal.sass";

/**
 * Confirmation with an illustration (hidden on mobile), a "Don't ask again"
 * checkbox and confirm / cancel actions.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => Promise<void> | void} props.onConfirm
 * @param {string} props.image
 * @param {string} props.title
 * @param {string} props.text
 * @param {string} props.confirmLabel
 * @param {string} props.cancelLabel
 * @param {string} props.skipFlag CONFIRM_SKIP_FLAG saved by "Don't ask again"
 */
export const ConfirmTradeModal = ({
  isOpen,
  onClose,
  onConfirm,
  image,
  title,
  text,
  confirmLabel,
  cancelLabel,
  skipFlag,
}) => {
  const { __ } = useTranslation();
  const { skipConfirm } = useTradeConfirmSkips();
  const [isDontAskAgain, setIsDontAskAgain] = useState(false);

  useEffect(() => {
    if (isOpen) setIsDontAskAgain(false);
  }, [isOpen]);

  const [isConfirming, handleConfirm] = usePendingAction(async () => {
    // Saved only once the action is confirmed, a cancel keeps asking
    if (isDontAskAgain) skipConfirm(skipFlag);

    await onConfirm();
  });

  return (
    <TradeModal
      isOpen={isOpen}
      onClose={onClose}
      className="confirm-trade-modal"
      labelledBy="confirm-trade-modal-title"
    >
      <img src={image} alt="" className="confirm-trade-modal__image" />

      <div className="confirm-trade-modal__body">
        <div className="confirm-trade-modal__copy">
          <h3
            id="confirm-trade-modal-title"
            className="confirm-trade-modal__title"
          >
            {title}
          </h3>
          <p className="confirm-trade-modal__text">{text}</p>
        </div>

        <label className="confirm-trade-modal__dont-ask">
          <button
            type="button"
            role="checkbox"
            aria-checked={isDontAskAgain}
            className={classnames("confirm-trade-modal__checkbox", {
              "confirm-trade-modal__checkbox--checked": isDontAskAgain,
            })}
            onClick={() => setIsDontAskAgain((prev) => !prev)}
          >
            {isDontAskAgain && <CheckboxCheckedIcon />}
          </button>
          {__("futures.trade_modals.dont_ask_again")}
        </label>
      </div>

      <div className="confirm-trade-modal__actions">
        <button
          type="button"
          className={classnames("confirm-trade-modal__confirm", {
            "confirm-trade-modal__confirm--loading": isConfirming,
          })}
          onClick={handleConfirm}
          disabled={isConfirming}
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          className="confirm-trade-modal__cancel"
          onClick={onClose}
        >
          {cancelLabel}
        </button>
      </div>
    </TradeModal>
  );
};
